<template>
  <div v-if="rendered" class="hc-root">
    <!-- HEADER -->
    <div class="hc-header">
      <div class="hc-header-left">
        <div class="hc-header-icon">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
          >
            <path d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
          </svg>
        </div>
        <div>
          <h1 class="hc-title">Histórico de Campanhas</h1>
          <p class="hc-sub">{{ total }} campanha{{ total !== 1 ? "s" : "" }}</p>
        </div>
      </div>
      <div class="hc-search-wrap">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          v-model="searchQuery"
          class="hc-search"
          placeholder="Pesquisar campanha…"
        />
        <button
          v-if="searchQuery"
          class="hc-search-x"
          @click="searchQuery = ''"
        >
          ✕
        </button>
      </div>
    </div>

    <!-- EMPTY -->
    <div v-if="campanhas.length === 0" class="hc-empty">
      <div class="hc-empty-icon">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
        </svg>
      </div>
      <h3>Nenhuma campanha enviada</h3>
      <p>As campanhas enviadas aparecerão aqui.</p>
    </div>

    <!-- TABELA -->
    <div v-else class="hc-table-wrap">
      <div class="hc-controls">
        <span class="hc-info">{{ from + 1 }}–{{ to }} de {{ total }}</span>
        <div class="hc-pag">
          <button
            class="hc-pag-btn"
            :disabled="pagination.currentPage <= 1"
            @click="pagination.currentPage--"
          >
            ‹
          </button>
          <button
            v-for="p in pageNums"
            :key="p"
            class="hc-pag-btn"
            :class="{ cur: p === pagination.currentPage }"
            @click="pagination.currentPage = p"
          >
            {{ p }}
          </button>
          <button
            class="hc-pag-btn"
            :disabled="pagination.currentPage >= totalPages"
            @click="pagination.currentPage++"
          >
            ›
          </button>
        </div>
      </div>

      <div class="hc-scroll">
        <table class="hc-table">
          <thead>
            <tr>
              <th class="hc-th">Campanha</th>
              <th class="hc-th hc-th-canal">Canal</th>
              <th class="hc-th hc-th-msg">Mensagem</th>
              <th class="hc-th hc-th-num">Destinatários</th>
              <th class="hc-th hc-th-custo">Custo aprox.</th>
              <th class="hc-th hc-th-date">Data</th>
              <th class="hc-th hc-th-time">Hora</th>
              <th class="hc-th hc-th-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in queriedData" :key="row.id" class="hc-row">
              <td class="hc-td hc-td-name">
                <span class="hc-camp-name">{{
                  row.titulo_campanha || "—"
                }}</span>
              </td>
              <td class="hc-td">
                <span class="hc-canal-badge" :class="row.canal">
                  <svg
                    v-if="row.canal === 'whatsapp'"
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                    />
                  </svg>
                  <svg
                    v-else
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {{ row.canal === "whatsapp" ? "WhatsApp" : "Push" }}
                </span>
              </td>
              <td class="hc-td hc-td-msg">
                <span class="hc-msg-preview">{{ row.mensagem }}</span>
              </td>
              <td class="hc-td hc-td-num">
                <span class="hc-num">{{
                  (row.total_destinatarios || 0).toLocaleString("pt-PT")
                }}</span>
              </td>
              <td class="hc-td hc-td-num">
                <span class="hc-custo">~ {{ calcularCusto(row) }}</span>
              </td>
              <td class="hc-td hc-td-date">{{ fmtDate(row.data_envio) }}</td>
              <td class="hc-td hc-td-time">{{ row.hora_envio || "—" }}</td>
              <td class="hc-td hc-td-actions">
                <div class="hc-actions-wrap">
                  <button
                    class="hc-action-btn hc-action-details"
                    @click="verDetalhes(row)"
                  >
                    Ver detalhes
                  </button>
                  <button
                    v-if="row.canal === 'push'"
                    class="hc-action-btn hc-action-clone"
                    @click="clonar(row)"
                  >
                    Clonar
                  </button>
                  <button
                    class="hc-action-btn hc-action-delete"
                    @click="confirmarApagar(row)"
                  >
                    Apagar
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL DETALHES -->
    <transition name="hc-fade">
      <div
        v-if="modalAberto"
        class="hc-overlay"
        @click.self="modalAberto = false"
      >
        <div class="hc-modal hc-modal-large">
          <!-- Cabeçalho -->
          <div class="hc-modal-head">
            <div>
              <div class="hc-modal-title">
                {{ campanhaSelecionada && campanhaSelecionada.titulo_campanha }}
              </div>
              <div class="hc-modal-sub">
                Destinatários e impacto no intervalo do evento
              </div>
            </div>
            <button class="hc-modal-close" @click="modalAberto = false">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="hc-modal-body">
            <!-- Info campanha -->
            <div class="hc-detail-grid" v-if="campanhaSelecionada">
              <div class="hc-detail-item">
                <span class="hc-detail-label">Canal</span>
                <span class="hc-canal-badge" :class="campanhaSelecionada.canal">
                  {{
                    campanhaSelecionada.canal === "whatsapp"
                      ? "WhatsApp"
                      : "Push"
                  }}
                </span>
              </div>
              <div class="hc-detail-item">
                <span class="hc-detail-label">Data de envio</span>
                <span class="hc-detail-val">{{
                  fmtDate(campanhaSelecionada.data_envio)
                }}</span>
              </div>
              <div class="hc-detail-item">
                <span class="hc-detail-label">Destinatários</span>
                <span class="hc-detail-val hc-detail-orange"
                  >{{
                    (
                      campanhaSelecionada.total_destinatarios || 0
                    ).toLocaleString("pt-PT")
                  }}
                  clientes</span
                >
              </div>
              <div
                class="hc-detail-item hc-detail-item--full"
                v-if="campanhaSelecionada.dia_evento"
              >
                <span class="hc-detail-label">Intervalo do evento</span>
                <div class="hc-date-edit-row">
                  <div class="hc-date-edit-group">
                    <label class="hc-date-edit-label">Início</label>
                    <input
                      type="date"
                      class="hc-date-input"
                      :value="editDiaInicio"
                      @input="editDiaInicio = $event.target.value"
                    />
                  </div>
                  <div class="hc-date-edit-group">
                    <label class="hc-date-edit-label">Fim</label>
                    <input
                      type="date"
                      class="hc-date-input"
                      :value="editDiaFim"
                      @input="editDiaFim = $event.target.value"
                    />
                  </div>
                  <button
                    class="hc-btn-atualizar"
                    :disabled="loadingAtualizarDatas"
                    @click="atualizarDatasEvento"
                  >
                    <span
                      v-if="loadingAtualizarDatas"
                      class="hc-spinner hc-spinner-sm"
                    ></span>
                    <span v-else>Atualizar</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loadingDetalhes" class="hc-loading-metricas">
              <span class="hc-spinner"></span> A carregar dados dos
              destinatários…
            </div>

            <template v-else-if="detalhes">
              <!-- Resumo do intervalo -->
              <div class="hc-metricas" v-if="detalhes.resumo">
                <p class="hc-metricas-title">Resumo no intervalo do evento</p>
                <div class="hc-metricas-grid">
                  <div class="hc-metrica">
                    <span class="hc-metrica-val">{{
                      resumoFiltrado.com_visita
                    }}</span>
                    <span class="hc-metrica-label">Regressaram</span>
                  </div>
                  <div class="hc-metrica">
                    <span class="hc-metrica-val">{{
                      resumoFiltrado.sem_visita
                    }}</span>
                    <span class="hc-metrica-label">Não regressaram</span>
                  </div>
                  <div class="hc-metrica">
                    <span class="hc-metrica-val">{{
                      resumoFiltrado.total_visitas
                    }}</span>
                    <span class="hc-metrica-label">Total visitas</span>
                  </div>
                  <div class="hc-metrica">
                    <span class="hc-metrica-val">{{
                      formatarEuro(resumoFiltrado.ticket_medio)
                    }}</span>
                    <span class="hc-metrica-label">Ticket médio</span>
                  </div>
                  <div class="hc-metrica hc-metrica-destaque">
                    <span class="hc-metrica-val">{{
                      formatarEuro(resumoFiltrado.total_gasto)
                    }}</span>
                    <span class="hc-metrica-label">Faturação total</span>
                  </div>
                </div>
              </div>

              <!-- Lista de destinatários -->
              <div
                class="hc-dest-section"
                v-if="detalhes.destinatarios && detalhes.destinatarios.length"
              >
                <div class="hc-dest-header">
                  <p class="hc-metricas-title mb-0">Destinatários</p>
                  <div class="hc-dest-controls">
                    <div class="hc-sort-group">
                      <button
                        class="hc-sort-btn"
                        :class="{ active: sortDestBy === 'retornou' }"
                        @click="sortDestBy = 'retornou'"
                      >
                        Regressaram
                      </button>
                      <button
                        class="hc-sort-btn"
                        :class="{ active: sortDestBy === 'visitas' }"
                        @click="sortDestBy = 'visitas'"
                      >
                        Mais visitas
                      </button>
                      <button
                        class="hc-sort-btn"
                        :class="{ active: sortDestBy === 'gasto' }"
                        @click="sortDestBy = 'gasto'"
                      >
                        Mais gasto
                      </button>
                    </div>
                    <select v-model="filtroAusencia" class="hc-ausencia-select">
                      <option :value="null">Todos</option>
                      <option :value="3">Não vinha há + 3 dias</option>
                      <option :value="7">Não vinha há + 1 semana</option>
                      <option :value="14">Não vinha há + 2 semanas</option>
                      <option :value="30">Não vinha há + 1 mês</option>
                      <option :value="60">Não vinha há + 2 meses</option>
                      <option :value="90">Não vinha há + 3 meses</option>
                      <option :value="180">Não vinha há + 6 meses</option>
                      <option :value="365">Não vinha há + 1 ano</option>
                    </select>
                    <input
                      v-model="searchDest"
                      class="hc-dest-search"
                      placeholder="Filtrar por nome…"
                    />
                  </div>
                </div>
                <div class="hc-dest-list">
                  <div
                    v-for="d in destinatariosFiltrados"
                    :key="d.qr_code"
                    class="hc-dest-row"
                    :class="{
                      'hc-dest-row--retornou': d.visitas_no_intervalo > 0,
                      'hc-dest-row--clickable': d.visitas_no_intervalo > 0,
                    }"
                    @click="d.visitas_no_intervalo > 0 && abrirConsumo(d)"
                  >
                    <!-- Avatar inicial -->
                    <div class="hc-dest-avatar">
                      {{ (d.nome || "?")[0].toUpperCase() }}
                    </div>
                    <div class="hc-dest-info">
                      <span class="hc-dest-nome">{{ d.nome || "—" }}</span>
                      <span class="hc-dest-email">{{
                        d.email || d.qr_code
                      }}</span>
                    </div>
                    <div class="hc-dest-stats">
                      <span
                        class="hc-dest-badge"
                        :class="
                          d.visitas_no_intervalo > 0
                            ? 'badge-green'
                            : 'badge-grey'
                        "
                      >
                        {{
                          d.visitas_no_intervalo > 0
                            ? `${d.visitas_no_intervalo} visita${
                                d.visitas_no_intervalo > 1 ? "s" : ""
                              }`
                            : "Não regressou"
                        }}
                      </span>
                      <span
                        v-if="d.gasto_no_intervalo > 0"
                        class="hc-dest-gasto"
                        >{{ formatarEuro(d.gasto_no_intervalo) }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="hc-modal-foot">
            <button class="hc-btn hc-btn-ghost" @click="modalAberto = false">
              Fechar
            </button>
            <button
              class="hc-btn hc-btn-danger"
              @click="confirmarApagar(campanhaSelecionada)"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4h6v2" />
              </svg>
              Apagar campanha
            </button>
            <button
              v-if="campanhaSelecionada && campanhaSelecionada.canal === 'push'"
              class="hc-btn hc-btn-primary"
              @click="
                clonar(campanhaSelecionada);
                modalAberto = false;
              "
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path
                  d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                />
              </svg>
              Clonar campanha
            </button>
          </div>
        </div>
      </div>
    </transition>
    <!-- MODAL CONSUMO CLIENTE -->
    <transition name="hc-fade">
      <div
        v-if="consumoModalAberto"
        class="hc-overlay hc-overlay-top"
        @click.self="consumoModalAberto = false"
      >
        <div class="hc-modal hc-modal-large">
          <div class="hc-modal-head">
            <div>
              <div class="hc-modal-title">Detalhe do cliente</div>
              <div class="hc-modal-sub">Consumos no intervalo do evento</div>
            </div>
            <button class="hc-modal-close" @click="consumoModalAberto = false">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="hc-modal-body" v-if="consumoCliente">
            <!-- Perfil do cliente -->
            <div class="hc-cliente-perfil">
              <div class="hc-cliente-avatar-lg">
                {{ (consumoCliente.nome || "?")[0].toUpperCase() }}
              </div>
              <div class="hc-cliente-info-main">
                <div class="hc-cliente-nome">
                  {{ consumoCliente.nome || "—" }}
                </div>
                <div class="hc-cliente-meta">
                  <span v-if="consumoCliente.email">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                      />
                      <polyline points="22,6 12,12 2,6" />
                    </svg>
                    {{ consumoCliente.email }}
                  </span>
                  <span v-if="consumoCliente.qr_code">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="2" y="2" width="8" height="8" />
                      <rect x="14" y="2" width="8" height="8" />
                      <rect x="2" y="14" width="8" height="8" />
                      <path
                        d="M14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z"
                      />
                    </svg>
                    Cartão {{ consumoCliente.qr_code }}
                  </span>
                </div>
              </div>
              <div class="hc-cliente-stats-row">
                <div class="hc-cliente-stat">
                  <span class="hc-cliente-stat-val">{{
                    consumoCliente.visitas_no_intervalo
                  }}</span>
                  <span class="hc-cliente-stat-label">Visitas</span>
                </div>
                <div class="hc-cliente-stat">
                  <span class="hc-cliente-stat-val">{{
                    formatarEuro(consumoCliente.gasto_no_intervalo)
                  }}</span>
                  <span class="hc-cliente-stat-label">Total gasto</span>
                </div>
                <div
                  class="hc-cliente-stat"
                  v-if="consumoCliente.visitas_no_intervalo > 0"
                >
                  <span class="hc-cliente-stat-val">{{
                    formatarEuro(
                      consumoCliente.gasto_no_intervalo /
                        consumoCliente.visitas_no_intervalo,
                    )
                  }}</span>
                  <span class="hc-cliente-stat-label">Ticket médio</span>
                </div>
              </div>
            </div>

            <!-- Lista de produtos consumidos -->
            <div
              v-if="consumoCliente.consumos && consumoCliente.consumos.length"
              class="hc-consumos-section"
            >
              <p class="hc-metricas-title">Produtos consumidos</p>
              <div class="hc-consumos-table-wrap">
                <table class="hc-consumos-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Categoria</th>
                      <th class="text-right">Qtd</th>
                      <th class="text-right">Pago</th>
                      <th>Restaurante</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(c, i) in consumoCliente.consumos" :key="i">
                      <td class="font-bold">{{ c.produto || "—" }}</td>
                      <td class="text-muted">{{ c.categoria || "—" }}</td>
                      <td class="text-right">{{ c.quantidade }}</td>
                      <td class="text-right font-bold text-orange">
                        {{ formatarEuro(c.pago) }}
                      </td>
                      <td class="text-muted">{{ c.restaurante || "—" }}</td>
                      <td class="text-muted">{{ fmtDate(c.data) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="hc-consumos-empty">
              Sem detalhe de produtos disponível para este cliente.
            </div>
          </div>
          <div class="hc-modal-foot">
            <button
              class="hc-btn hc-btn-ghost"
              @click="consumoModalAberto = false"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
  <LoadingOverlay v-else />
</template>

<script>
  import LoadingOverlay from "@/components/Loading/LoadingOverlay";
  import { campanhasService } from "@/services";
  import swal from "sweetalert2";

  export default {
    components: { LoadingOverlay },
    data() {
      return {
        rendered: false,
        campanhas: [],
        searchQuery: "",
        searchDest: "",
        modalAberto: false,
        campanhaSelecionada: null,
        detalhes: null,
        loadingDetalhes: false,
        loadingAtualizarDatas: false,
        editDiaInicio: null,
        editDiaFim: null,
        consumoModalAberto: false,
        consumoCliente: null,
        sortDestBy: "retornou", // "retornou" | "visitas" | "gasto"
        filtroAusencia: null, // null | dias
        pagination: { perPage: 15, currentPage: 1 },
      };
    },
    computed: {
      filteredData() {
        if (!this.searchQuery) return this.campanhas;
        const q = this.searchQuery.toLowerCase();
        return this.campanhas.filter(
          (c) =>
            c.titulo_campanha?.toLowerCase().includes(q) ||
            c.mensagem?.toLowerCase().includes(q),
        );
      },
      total() {
        return this.filteredData.length;
      },
      totalPages() {
        return Math.ceil(this.total / this.pagination.perPage) || 1;
      },
      from() {
        return (this.pagination.currentPage - 1) * this.pagination.perPage;
      },
      to() {
        return Math.min(this.from + this.pagination.perPage, this.total);
      },
      queriedData() {
        return this.filteredData.slice(this.from, this.to);
      },
      pageNums() {
        const p = this.pagination.currentPage,
          t = this.totalPages,
          r = [];
        for (let i = Math.max(1, p - 2); i <= Math.min(t, p + 2); i++)
          r.push(i);
        return r;
      },
      resumoFiltrado() {
        const lista = this.destinatariosFiltrados;
        if (!lista.length)
          return {
            com_visita: 0,
            sem_visita: 0,
            total_visitas: 0,
            total_gasto: 0,
            ticket_medio: 0,
          };
        const com_visita = lista.filter(
          (d) => d.visitas_no_intervalo > 0,
        ).length;
        const sem_visita = lista.filter(
          (d) => d.visitas_no_intervalo === 0,
        ).length;
        const total_visitas = lista.reduce(
          (s, d) => s + d.visitas_no_intervalo,
          0,
        );
        const total_gasto = parseFloat(
          lista.reduce((s, d) => s + d.gasto_no_intervalo, 0).toFixed(2),
        );
        const ticket_medio =
          total_visitas > 0
            ? parseFloat((total_gasto / total_visitas).toFixed(2))
            : 0;
        return {
          com_visita,
          sem_visita,
          total_visitas,
          total_gasto,
          ticket_medio,
        };
      },

      destinatariosFiltrados() {
        if (!this.detalhes?.destinatarios) return [];

        const dataEnvio = this.campanhaSelecionada?.data_envio
          ? new Date(this.campanhaSelecionada.data_envio)
          : null;

        let lista = this.detalhes.destinatarios;

        // Filtro por ausência antes do envio
        if (this.filtroAusencia !== null && dataEnvio) {
          lista = lista.filter((d) => {
            if (!d.ultima_visita_antes_envio) return true; // nunca veio = ausência infinita
            const ultimaVisita = new Date(d.ultima_visita_antes_envio);
            const diffDias = Math.floor(
              (dataEnvio - ultimaVisita) / (1000 * 60 * 60 * 24),
            );
            return diffDias > this.filtroAusencia;
          });
        }

        // Filtro por nome
        if (this.searchDest) {
          const q = this.searchDest.toLowerCase();
          lista = lista.filter(
            (d) =>
              (d.nome || "").toLowerCase().includes(q) ||
              (d.email || "").toLowerCase().includes(q),
          );
        }

        return [...lista].sort((a, b) => {
          if (this.sortDestBy === "visitas")
            return b.visitas_no_intervalo - a.visitas_no_intervalo;
          if (this.sortDestBy === "gasto")
            return b.gasto_no_intervalo - a.gasto_no_intervalo;
          return (
            (b.visitas_no_intervalo > 0 ? 1 : 0) -
            (a.visitas_no_intervalo > 0 ? 1 : 0)
          );
        });
      },
    },
    watch: {
      searchQuery() {
        this.pagination.currentPage = 1;
      },
    },
    methods: {
      fmtDate(d) {
        if (!d) return "—";
        const s = typeof d === "string" ? d : new Date(d).toISOString();
        return s.slice(0, 10).split("-").reverse().join("/");
      },
      formatarEuro(valor) {
        return new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
        }).format(valor || 0);
      },
      calcularCusto(row) {
        const n = Number(row.total_destinatarios) || 0;
        if (!n) return "—";
        const euros =
          row.canal === "whatsapp" ? n * 0.03 : n * 3 * (91.74 / 20000);
        return new Intl.NumberFormat("pt-PT", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(euros);
      },
      clonar(row) {
        this.$router.push({
          path: "/campanhas/criar",
          query: { campaign_cloned_id: row.id },
        });
      },
      async verDetalhes(campanha) {
        this.campanhaSelecionada = campanha;
        this.detalhes = null;
        this.searchDest = "";
        this.loadingDetalhes = true;
        this.modalAberto = true;
        // Preencher inputs de data com valores atuais
        this.filtroAusencia = null;
        this.editDiaInicio = campanha.dia_evento?.inicio
          ? campanha.dia_evento.inicio.slice(0, 10)
          : "";
        this.editDiaFim = campanha.dia_evento?.fim
          ? campanha.dia_evento.fim.slice(0, 10)
          : "";

        try {
          const data = await campanhasService.getDetalhesCampanha(campanha.id);
          if (data.success) {
            this.detalhes = data;
          }
        } catch (err) {
          console.error("Erro ao carregar detalhes:", err);
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível carregar os detalhes.",
          });
        } finally {
          this.loadingDetalhes = false;
        }
      },
      async confirmarApagar(campanha) {
        const resultado = await swal.fire({
          title: "Apagar campanha?",
          html: `Tens a certeza que queres apagar <strong>${
            campanha.titulo_campanha || "esta campanha"
          }</strong>?<br><small style="color:#b0a8a0">Esta ação não pode ser revertida.</small>`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, apagar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#e53e3e",
          cancelButtonColor: "#f7f4f1",
        });

        if (!resultado.value) return;

        try {
          await campanhasService.apagarCampanha(campanha.id);
          this.campanhas = this.campanhas.filter((c) => c.id !== campanha.id);
          if (
            this.modalAberto &&
            this.campanhaSelecionada?.id === campanha.id
          ) {
            this.modalAberto = false;
          }
          this.$notify({
            type: "success",
            title: "Apagado",
            message: "Campanha apagada com sucesso.",
          });
        } catch (err) {
          console.error("Erro ao apagar campanha:", err);
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível apagar a campanha.",
          });
        }
      },

      abrirConsumo(cliente) {
        this.consumoCliente = cliente;
        this.consumoModalAberto = true;
      },

      async atualizarDatasEvento() {
        if (!this.campanhaSelecionada || !this.editDiaInicio) return;
        this.loadingAtualizarDatas = true;
        try {
          await campanhasService.atualizarDatasEvento(
            this.campanhaSelecionada.id,
            {
              inicio: this.editDiaInicio,
              fim: this.editDiaFim || null,
            },
          );
          // Atualizar localmente sem refresh
          if (!this.campanhaSelecionada.dia_evento) {
            this.campanhaSelecionada.dia_evento = {};
          }
          this.campanhaSelecionada.dia_evento.inicio = this.editDiaInicio;
          this.campanhaSelecionada.dia_evento.fim = this.editDiaFim || null;
          // Recarregar detalhes para atualizar lista e métricas
          this.loadingDetalhes = true;
          const data = await campanhasService.getDetalhesCampanha(
            this.campanhaSelecionada.id,
          );
          if (data.success) this.detalhes = data;
          this.$notify({
            type: "success",
            title: "Atualizado",
            message: "Datas do evento atualizadas.",
          });
        } catch (err) {
          console.error("Erro ao atualizar datas:", err);
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível atualizar as datas.",
          });
        } finally {
          this.loadingAtualizarDatas = false;
          this.loadingDetalhes = false;
        }
      },

      async inicio() {
        try {
          const resp = await campanhasService.getHistorico();
          this.campanhas = resp.success && resp.campanhas ? resp.campanhas : [];
        } catch (err) {
          console.error("Erro:", err);
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível carregar o histórico.",
          });
        }
        this.rendered = true;
      },
    },
    async mounted() {
      await this.inicio();
    },
  };
</script>

<style scoped>
  .hc-root {
    padding: 28px 28px 48px;
    font-family: "DM Sans", "Outfit", system-ui, sans-serif;
    min-height: 100vh;
    background: #f8f6f3;
  }

  /* HEADER */
  .hc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    gap: 16px;
    flex-wrap: wrap;
  }
  .hc-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .hc-header-icon {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    background: #ff825d;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 4px 14px rgba(255, 130, 93, 0.3);
  }
  .hc-title {
    font-size: 20px;
    font-weight: 800;
    color: #1a1210;
    margin: 0;
    letter-spacing: -0.4px;
  }
  .hc-sub {
    font-size: 13px;
    color: #b0a8a0;
    margin: 3px 0 0;
  }
  .hc-search-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1.5px solid #f0ece8;
    border-radius: 11px;
    padding: 9px 13px;
    transition: border-color 0.15s;
  }
  .hc-search-wrap:focus-within {
    border-color: #ff825d;
  }
  .hc-search {
    background: none;
    border: none;
    outline: none;
    font-size: 13.5px;
    color: #1a1210;
    width: 200px;
    font-family: inherit;
  }
  .hc-search::placeholder {
    color: #c0b8b0;
  }
  .hc-search-x {
    background: none;
    border: none;
    cursor: pointer;
    color: #c0b8b0;
    font-size: 12px;
    padding: 0;
  }

  /* EMPTY */
  .hc-empty {
    text-align: center;
    padding: 80px 20px;
    color: #b0a8a0;
  }
  .hc-empty-icon {
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: #f5f2ef;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: #c8c0b8;
  }
  .hc-empty h3 {
    font-size: 18px;
    font-weight: 700;
    color: #5a534f;
    margin-bottom: 8px;
  }
  .hc-empty p {
    font-size: 14px;
    color: #b0a8a0;
    margin: 0;
  }

  /* TABLE */
  .hc-table-wrap {
    background: #fff;
    border: 1.5px solid #f0ece8;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 14px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }
  .hc-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 18px;
    border-bottom: 1px solid #f5f2ef;
    flex-wrap: wrap;
    gap: 10px;
  }
  .hc-info {
    font-size: 13px;
    color: #b0a8a0;
  }
  .hc-pag {
    display: flex;
    gap: 3px;
  }
  .hc-pag-btn {
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    border: 1.5px solid #e8e4e0;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
    color: #6b6460;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    font-family: inherit;
  }
  .hc-pag-btn:hover:not(:disabled) {
    border-color: #ff825d;
    color: #ff825d;
  }
  .hc-pag-btn:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .hc-pag-btn.cur {
    background: #ff825d;
    border-color: #ff825d;
    color: #fff;
    font-weight: 700;
  }
  .hc-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .hc-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }
  .hc-th {
    padding: 10px 16px;
    background: #faf8f6;
    border-bottom: 1px solid #f0ece8;
    font-size: 11px;
    font-weight: 700;
    color: #b0a8a0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-align: left;
    white-space: nowrap;
  }
  .hc-th-canal {
    width: 110px;
  }
  .hc-th-msg {
    min-width: 220px;
  }
  .hc-th-num {
    width: 120px;
    text-align: center;
  }
  .hc-th-custo {
    width: 120px;
    text-align: center;
  }
  .hc-th-date {
    width: 110px;
  }
  .hc-th-time {
    width: 90px;
  }
  .hc-th-actions {
    width: 180px;
  }
  .hc-row {
    border-bottom: 1px solid #f8f5f2;
    transition: background 0.08s;
  }
  .hc-row:last-child {
    border-bottom: none;
  }
  .hc-row:hover {
    background: #fdfcfb;
  }
  .hc-td {
    padding: 13px 16px;
    vertical-align: middle;
    color: #2a2420;
  }
  .hc-td-num {
    text-align: center;
  }
  .hc-td-date,
  .hc-td-time {
    color: #8a857e;
    font-size: 13px;
    white-space: nowrap;
  }
  .hc-camp-name {
    font-weight: 600;
    color: #1a1210;
  }
  .hc-msg-preview {
    display: block;
    color: #7a736e;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 280px;
  }
  .hc-num {
    font-weight: 700;
    color: #1a1210;
  }
  .hc-custo {
    font-weight: 600;
    color: #8a857e;
    font-size: 13px;
  }
  .hc-canal-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 700;
  }
  .hc-canal-badge.whatsapp {
    background: #e8fdf0;
    color: #0a7a4a;
  }
  .hc-canal-badge.push {
    background: #fff0eb;
    color: #c04820;
  }
  .hc-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 11px;
    border: none;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.12s;
    font-family: inherit;
    margin-right: 4px;
  }
  .hc-action-details {
    background: #f7f4f1;
    color: #5a534f;
  }
  .hc-action-details:hover {
    background: #fff0eb;
    color: #ff825d;
  }
  .hc-action-clone {
    background: #f7f4f1;
    color: #5a534f;
  }
  .hc-action-clone:hover {
    background: #f0ece8;
  }

  /* MODAL */
  .hc-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 10, 8, 0.45);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1200;
    padding: 16px;
  }
  .hc-modal {
    background: #fff;
    border-radius: 18px;
    width: 100%;
    max-width: 520px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }
  .hc-modal-large {
    max-width: 1020px;
  }
  .hc-modal-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #f5f2ef;
    gap: 14px;
  }
  .hc-modal-title {
    font-size: 16px;
    font-weight: 800;
    color: #1a1210;
    letter-spacing: -0.3px;
  }
  .hc-modal-sub {
    font-size: 13px;
    color: #b0a8a0;
    margin-top: 3px;
  }
  .hc-modal-close {
    background: #f7f4f1;
    border: none;
    cursor: pointer;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7a736e;
    transition: all 0.12s;
    flex-shrink: 0;
  }
  .hc-modal-close:hover {
    background: #f0ece8;
    color: #1a1210;
  }
  .hc-modal-body {
    padding: 20px 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
  }

  /* Detail grid */
  .hc-detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .hc-detail-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .hc-detail-label {
    font-size: 11px;
    font-weight: 700;
    color: #b0a8a0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .hc-detail-val {
    font-size: 14px;
    font-weight: 600;
    color: #1a1210;
  }
  .hc-detail-orange {
    color: #ff825d;
  }

  /* Loading */
  .hc-loading-metricas {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #b0a8a0;
    padding: 16px 0;
  }
  .hc-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #e8e4e0;
    border-top-color: #ff825d;
    border-radius: 50%;
    animation: hc-spin 0.6s linear infinite;
    flex-shrink: 0;
  }
  @keyframes hc-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Métricas */
  .hc-metricas {
    background: #faf8f6;
    border-radius: 12px;
    padding: 16px 18px;
  }
  .hc-metricas-title {
    font-size: 11px;
    font-weight: 700;
    color: #b0a8a0;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    margin-bottom: 14px;
  }
  .hc-metricas-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
  }
  .hc-metrica {
    text-align: center;
  }
  .hc-metrica-val {
    display: block;
    font-size: 18px;
    font-weight: 800;
    color: #1a1210;
    letter-spacing: -0.5px;
  }
  .hc-metrica-label {
    display: block;
    font-size: 11px;
    color: #b0a8a0;
    margin-top: 3px;
  }
  .hc-metrica-destaque .hc-metrica-val {
    color: #ff825d;
    font-size: 20px;
  }

  /* Destinatários */
  .hc-dest-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .hc-dest-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .hc-dest-search {
    background: #f8f6f3;
    border: 1.5px solid #f0ece8;
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 13px;
    outline: none;
    font-family: inherit;
    color: #1a1210;
    transition: border-color 0.15s;
  }
  .hc-dest-search:focus {
    border-color: #ff825d;
  }
  .hc-dest-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 320px;
    overflow-y: auto;
    padding-right: 4px;
  }
  .hc-dest-list::-webkit-scrollbar {
    width: 4px;
  }
  .hc-dest-list::-webkit-scrollbar-thumb {
    background: #e8e4e0;
    border-radius: 2px;
  }
  .hc-dest-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #faf8f6;
    border: 1.5px solid #f0ece8;
    transition: border-color 0.12s;
  }
  .hc-dest-row--retornou {
    border-color: rgba(10, 122, 74, 0.2);
    background: #f5fdf9;
  }
  .hc-dest-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #f0ece8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: #7a736e;
    flex-shrink: 0;
  }
  .hc-dest-info {
    flex: 1;
    min-width: 0;
  }
  .hc-dest-nome {
    display: block;
    font-size: 13.5px;
    font-weight: 600;
    color: #1a1210;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .hc-dest-email {
    display: block;
    font-size: 12px;
    color: #b0a8a0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .hc-dest-stats {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .hc-dest-badge {
    padding: 3px 8px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 700;
  }
  .badge-green {
    background: #e8faf2;
    color: #0a7a4a;
  }
  .badge-grey {
    background: #f0ece8;
    color: #a09890;
  }
  .hc-dest-gasto {
    font-size: 12.5px;
    font-weight: 700;
    color: #ff825d;
  }

  /* Footer */
  .hc-modal-foot {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding: 16px 24px;
    border-top: 1px solid #f5f2ef;
  }
  .hc-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 18px;
    border: none;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;
  }
  .hc-btn-ghost {
    background: #f7f4f1;
    color: #5a534f;
  }
  .hc-btn-ghost:hover {
    background: #f0ece8;
  }
  .hc-btn-primary {
    background: #ff825d;
    color: #fff;
    box-shadow: 0 2px 8px rgba(255, 130, 93, 0.3);
  }
  .hc-btn-primary:hover {
    background: #e86b45;
  }

  /* Transition */
  .hc-fade-enter-active {
    transition: opacity 0.2s;
  }
  .hc-fade-leave-active {
    transition: opacity 0.15s;
  }
  .hc-fade-enter,
  .hc-fade-leave-to {
    opacity: 0;
  }

  /* Actions wrap */
  .hc-actions-wrap {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: flex-start;
  }

  /* Date edit */
  .hc-detail-item--full {
    grid-column: 1 / -1;
  }
  .hc-date-edit-row {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }
  .hc-date-edit-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .hc-date-edit-label {
    font-size: 11px;
    color: #b0a8a0;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .hc-date-input {
    border: 1.5px solid #e8e4e0;
    border-radius: 8px;
    padding: 7px 10px;
    font-size: 13px;
    font-family: inherit;
    color: #1a1210;
    outline: none;
    transition: border-color 0.15s;
    background: #fff;
  }
  .hc-date-input:focus {
    border-color: #ff825d;
  }
  .hc-btn-atualizar {
    padding: 8px 14px;
    background: #ff825d;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }
  .hc-btn-atualizar:hover:not(:disabled) {
    background: #e86b45;
  }
  .hc-btn-atualizar:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .hc-spinner-sm {
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: hc-spin 0.6s linear infinite;
    display: inline-block;
  }

  /* Clickable dest row */
  .hc-dest-row--clickable {
    cursor: pointer;
  }
  .hc-dest-row--clickable:hover {
    border-color: rgba(10, 122, 74, 0.4);
    background: #edfaf4;
  }

  /* Consumo modal */
  .hc-overlay-top {
    z-index: 1300;
  }
  .hc-consumo-grid {
    display: flex;
    gap: 16px;
    background: #faf8f6;
    border-radius: 12px;
    padding: 16px;
    justify-content: space-around;
    text-align: center;
  }
  .hc-consumo-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .hc-consumo-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .hc-consumo-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0ece8;
    font-size: 13.5px;
  }
  .hc-consumo-label {
    color: #b0a8a0;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .hc-consumo-val {
    color: #1a1210;
    font-weight: 600;
  }

  /* Ausencia select */
  .hc-ausencia-select {
    border: 1.5px solid #e8e4e0;
    border-radius: 20px;
    background: #fff;
    font-size: 12px;
    font-weight: 600;
    color: #7a736e;
    cursor: pointer;
    font-family: inherit;
    padding: 5px 28px 5px 11px;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23b0a8a0' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    transition: border-color 0.15s;
  }
  .hc-ausencia-select:focus {
    border-color: #ff825d;
  }

  /* Delete button */
  .hc-action-delete {
    background: #fef2f2;
    color: #e53e3e;
  }
  .hc-action-delete:hover {
    background: #fee2e2;
    color: #c53030;
  }
  .hc-btn-danger {
    background: #fff0f0;
    color: #e53e3e;
    border: 1.5px solid #fed7d7;
  }
  .hc-btn-danger:hover {
    background: #fee2e2;
    color: #c53030;
    border-color: #feb2b2;
  }

  /* Sort controls */
  .hc-dest-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .hc-sort-group {
    display: flex;
    gap: 4px;
  }
  .hc-sort-btn {
    padding: 5px 11px;
    border: 1.5px solid #e8e4e0;
    border-radius: 20px;
    background: #fff;
    font-size: 12px;
    font-weight: 600;
    color: #7a736e;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
    white-space: nowrap;
  }
  .hc-sort-btn:hover {
    border-color: #ff825d;
    color: #ff825d;
  }
  .hc-sort-btn.active {
    background: #ff825d;
    border-color: #ff825d;
    color: #fff;
  }

  /* Consumos table */
  .hc-consumos-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .hc-consumos-table-wrap {
    overflow-x: auto;
    border: 1.5px solid #f0ece8;
    border-radius: 10px;
  }
  .hc-consumos-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .hc-consumos-table thead tr {
    background: #faf8f6;
  }
  .hc-consumos-table th {
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 700;
    color: #b0a8a0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-align: left;
    white-space: nowrap;
    border-bottom: 1px solid #f0ece8;
  }
  .hc-consumos-table td {
    padding: 9px 12px;
    border-bottom: 1px solid #f8f5f2;
    color: #2a2420;
    vertical-align: middle;
  }
  .hc-consumos-table tr:last-child td {
    border-bottom: none;
  }
  .hc-consumos-table .text-right {
    text-align: right;
  }
  .hc-consumos-table .text-muted {
    color: #8a857e;
  }
  .hc-consumos-table .font-bold {
    font-weight: 700;
  }
  .hc-consumos-table .text-orange {
    color: #ff825d;
  }
  .hc-consumos-empty {
    font-size: 13px;
    color: #b0a8a0;
    text-align: center;
    padding: 16px 0;
  }

  /* Cliente perfil card */
  .hc-cliente-perfil {
    display: flex;
    align-items: center;
    gap: 16px;
    background: linear-gradient(135deg, #fff8f6 0%, #fff3ef 100%);
    border: 1.5px solid #ffe0d6;
    border-radius: 14px;
    padding: 18px 20px;
    flex-wrap: wrap;
  }
  .hc-cliente-avatar-lg {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #ff825d;
    color: #fff;
    font-size: 20px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(255, 130, 93, 0.3);
  }
  .hc-cliente-info-main {
    flex: 1;
    min-width: 0;
  }
  .hc-cliente-nome {
    font-size: 17px;
    font-weight: 800;
    color: #1a1210;
    letter-spacing: -0.3px;
    margin-bottom: 5px;
  }
  .hc-cliente-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  .hc-cliente-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12.5px;
    color: #7a736e;
  }
  .hc-cliente-meta svg {
    flex-shrink: 0;
    color: #b0a8a0;
  }
  .hc-cliente-stats-row {
    display: flex;
    gap: 20px;
    flex-shrink: 0;
  }
  .hc-cliente-stat {
    text-align: center;
  }
  .hc-cliente-stat-val {
    display: block;
    font-size: 16px;
    font-weight: 800;
    color: #ff825d;
    letter-spacing: -0.4px;
  }
  .hc-cliente-stat-label {
    display: block;
    font-size: 11px;
    color: #b0a8a0;
    margin-top: 2px;
  }

  @media (max-width: 600px) {
    .hc-root {
      padding: 16px 12px 40px;
    }
    .hc-search {
      width: 140px;
    }
    .hc-detail-grid {
      grid-template-columns: 1fr;
    }
    .hc-metricas-grid {
      grid-template-columns: 1fr 1fr;
    }
    .hc-dest-controls {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
